import React from "react";

import { ReactComponent as BustleTick } from "@images/bustle_tick_icon.svg";
import classNames from "classnames";
import { stat } from "fs";
import { keyBy } from "lodash";
import { useTranslation } from "react-i18next";

import { Price } from "../Atoms";
import { bustleBeeForCustomer, BustleBee } from "../Customer/bustleBees";
import { useLoadedCustomer } from "../Customer/queries";
import { Customer } from "../Customer/schema";
import { OrderItem } from "../CustomerTab/schema";
import { BillSplit, OrderItemClaim, OrderItemStatus } from "./schema";

export const OrderItemSwitch = ({
  orderItem,
  status,
  disabled,
  billSplit,
  deleteOrderItemClaim,
  unclaimable,
  claim,
  claimItem,
}: {
  orderItem: OrderItem;
  claim?: OrderItemClaim & { customer: Customer };
  status: OrderItemStatus;
  claimItem: () => void;
  billSplit: BillSplit;
  deleteOrderItemClaim: () => void;
  disabled?: boolean;
  unclaimable?: boolean;
}) => {
  const { t } = useTranslation();
  const bustleBee = claim && bustleBeeForCustomer(claim.customer);
  switch (status) {
    case "yours":
      return (
        <ClaimBox
          status={status}
          ourClaim
          disabled={disabled}
          onClick={deleteOrderItemClaim}
        >
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              <div className="text-venue-tertiary w-32 h-32">
                <BustleTick />
              </div>
            }
          />
        </ClaimBox>
      );

    case "others_claimed":
      return claim ? (
        <ClaimBox status={status} theirClaim disabled>
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              bustleBee && (
                <div className="flex items-center w-full justify-end gap-8">
                  <div className="flex-1">Claimed </div>
                  <BustleBeeIcon bustleBee={bustleBee} />
                </div>
              )
            }
          />
        </ClaimBox>
      ) : null;
    case "others_paid":
      return claim ? (
        <ClaimBox status={status} theirClaim disabled>
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              bustleBee && (
                <div className="flex items-center w-full justify-end gap-8">
                  <div className="flex-1">Paid </div>
                  <BustleBeeIcon bustleBee={bustleBee} />
                </div>
              )
            }
          />
        </ClaimBox>
      ) : null;
    case "paid_at_till":
      return (
        <ClaimBox status={status} theirClaim disabled>
          <ClaimableOrderItemContent
            orderItem={orderItem}
            claimedContent={
              <div className=""> {t("billSplit.paidAtTill")}</div>
            }
          />
        </ClaimBox>
      );
    case "unclaimed":
      return (
        <div className="space-y-4">
          <ClaimBox
            status={unclaimable ? "unclaimable" : status}
            onClick={claimItem}
            disabled={disabled || unclaimable}
            warningMessage={
              unclaimable ? t("billSplit.unclaimable") : undefined
            }
          >
            <ClaimableOrderItemContent orderItem={orderItem} />
          </ClaimBox>
        </div>
      );
  }
};
export const ClaimableOrderItemContent = ({
  orderItem,
  claimedContent,
  warningMessage,
}: {
  orderItem: OrderItem;
  claimedContent?: React.ReactNode;
  warningMessage?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center gap-12 justify-between w-full">
        {orderItem.itemHeroImageThumbnailUrl && (
          <div className="flex-none">
            <div className="  flex-none aspect-w-1 aspect-h-1 w-[80px] ">
              <img
                src={orderItem.itemHeroImageThumbnailUrl}
                className="rounded-venue-brand object-cover"
              />
            </div>
          </div>
        )}
        <div className="flex-1">
          <div className="font-bold">
            {t("billSplit.itemNameWithMultiplier", {
              itemName: orderItem.itemName,
              count: orderItem.multiplier,
            })}
          </div>
          <div>
            <Price value={orderItem.totalSalesPriceTaxInclusive} />
          </div>
          <ul className="text-xs font-normal list-disc wrap">
            {orderItem.orderItemMods.map((mod) => mod.name).join(", ")}
          </ul>
        </div>
        <div className="w-1/8 text-right justify-self-end flex justify-end">
          {claimedContent || <div className="w-32 h-32" />}
        </div>
      </div>
      {warningMessage && (
        <div className="absolute inset-0 text-center font-normal text-sm flex justify-center items-end">
          <div className="bg-venue-tertiary opacity-80 absolute inset-0" />
          <div className="relative bg-venue-tertiary p-8">
            {warningMessage && warningMessage}{" "}
          </div>
        </div>
      )}
    </>
  );
};

export const ClaimBox = ({
  children,
  onClick,
  status,
  disabled,
  ourClaim,
  theirClaim,
  warningMessage,
}: {
  children: React.ReactNode;
  status?: OrderItemStatus;
  disabled?: boolean;
  onClick?: () => void;
  ourClaim?: boolean;
  theirClaim?: boolean;
  warningMessage?: React.ReactNode;
}) => {
  const unclaimed = !ourClaim && !theirClaim && !disabled;
  const grayedOut =
    !warningMessage &&
    (status == "others_claimed" ||
      status == "others_paid" ||
      status == "paid_at_till" ||
      status == "unclaimable");
  return (
    <div className="relative">
      <div
        onClick={!disabled && !theirClaim && onClick ? onClick : undefined}
        className={classNames(
          "relative border-2 flex shadow-venue-brand-style rounded-venue-brand p-12 flex-wrap text-base font-medium items-center justify-between gap-8",
          {
            "bg-venue-tertiary border-venue-tertiary": !status,
            "cursor-pointer hover:border-venue-primary bg-venue-primary border-venue-primary text-venue-tertiary":
              status == "yours",
            "bg-venue-tertiary border-venue-tertiary cursor-pointer ":
              !disabled && status == "unclaimed",
            "claimed bg-venue-tertiary border-venue-tertiary grayscale":
              status == "others_claimed",
            "paid bg-venue-tertiary border-venue-tertiary grayscale":
              status == "paid_at_till" || status == "others_paid",
            "unclaimable bg-venue-tertiary border-venue-tertiary grayscale   ":
              status == "unclaimable",
          },
        )}
      >
        {children}
      </div>
      <div
        className={classNames({
          "absolute inset-0 text-center font-normal text-sm flex justify-center items-end bg-neutral-300 opacity-30 rounded-venue-brand":
            grayedOut,
        })}
      />
      {warningMessage && (
        <div className="absolute inset-0 text-center font-normal text-xs flex justify-center items-end">
          <div className="bg-neutral-300 opacity-30 absolute inset-0 rounded-venue-brand" />
          <div className="relative bg-venue-tertiary rounded-b-venue-brand p-8 flex-1">
            {warningMessage && warningMessage}{" "}
          </div>
        </div>
      )}
    </div>
  );
};

export const RemainderClaim = ({
  billSplit,
  customer,
  onClaimRemainder,
  unclaimedTotal,
}: {
  billSplit: BillSplit;
  customer: Customer;
  onClaimRemainder: (customer: Customer | null) => void;
  unclaimedTotal: number;
}) => {
  const { t } = useTranslation();
  const customers = keyBy(billSplit.customers, "uuid");
  const bustleBee =
    billSplit.remainderClaimerId &&
    bustleBeeForCustomer(customers[billSplit.remainderClaimerId]);

  const claimingCustomer =
    billSplit.remainderClaimerId && customers[billSplit.remainderClaimerId];
  const owner = customers[billSplit.ownerId];
  const ownerBuslteBee = bustleBeeForCustomer(owner);
  const ourClaim = billSplit.remainderClaimerId === customer.uuid;
  const theirClaim = !!billSplit.remainderClaimerId && !ourClaim;
  const hasBeenClaimed = !!billSplit.remainderClaimerId;
  const status: OrderItemStatus = hasBeenClaimed
    ? ourClaim
      ? "yours"
      : "others_claimed"
    : "unclaimed";

  const isOwner = billSplit.ownerId === customer.uuid;
  return (
    <ClaimBox
      status={status}
      ourClaim={ourClaim}
      theirClaim={theirClaim}
      onClick={() => {
        onClaimRemainder(!billSplit.remainderClaimerId ? customer : null);
      }}
    >
      <div className="flex items-center gap-12 justify-between w-full">
        <div>
          <h3 className="font-bold text-xl">{t("billSplit.totalRemaining")}</h3>
          <Price value={unclaimedTotal} />
          <div className="flex-1 ">
            <div>
              {isOwner
                ? t("billSplit.totalRemaining_ownerDescription")
                : t("billSplit.totalRemaining_description")}
            </div>
          </div>
        </div>
        <div className="w-1/8 text-right justify-self-end flex justify-end">
          {ourClaim && (
            <div className="text-venue-tertiary w-48 h-48 p-4">
              <BustleTick />
            </div>
          )}
          {!ourClaim && hasBeenClaimed && bustleBee && (
            <BustleBeeIcon bustleBee={bustleBee} />
          )}
        </div>
      </div>
      <div className="mx-auto text-center text-lg font-bold">
        {!hasBeenClaimed && t("billSplit.claimRemainder")}
        {claimingCustomer &&
          theirClaim &&
          t("billSplit.remainderClaimedBy", {
            name: claimingCustomer.nickname,
          })}
        {hasBeenClaimed && ourClaim && t("billSplit.youHaveClaimedRemainder")}
      </div>
    </ClaimBox>
  );
};

const BustleBeeIcon = ({ bustleBee }: { bustleBee: BustleBee }) => {
  return (
    <div className="rounded-full bg-white w-48 h-48 p-4 ">
      <img src={bustleBee.img_96} alt={bustleBee.alt} />
    </div>
  );
};

export const UnclaimedOrderItem = ({
  orderItem,
  claimItem,
  disabled,
}: {
  orderItem: OrderItem;
  claimItem: () => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <ClaimBox onClick={claimItem} disabled={disabled}>
      <ClaimableOrderItemContent orderItem={orderItem} />
    </ClaimBox>
  );
};
